import React from 'react'

import { Box, Text, TogglerCheckbox } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { FormErrors } from 'modules/core/hooks/useForm'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'

import { useHasContractCue } from 'modules/contracts/hooks/useHasContractCue'
import { BasicInformationValues } from 'modules/employees/components/Personal/NewPersonal/BasicInformation/types'
import type Employee from 'modules/employees/store/models/Employee'
import { Country } from 'modules/legal_entities/lib/constants'
import getMaxLengthByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/fieldLength'
import getPlaceholderByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/placeholders'
import { SpainIdType } from 'modules/legal_entities/lib/identifierValidators/spain/idType'
import type { IdentificationType } from 'modules/legal_entities/lib/types'
import Identifiers from 'modules/payroll_employees/components/Identifiers'

interface Props {
  values: BasicInformationValues
  onChangeValue: (fieldName: string, fieldValue: string | undefined | boolean) => void
  isSpainCompany: boolean
  readonly: boolean
  employee: Employee
  errors: FormErrors<BasicInformationValues>
  setIdentifiersDirty: React.Dispatch<React.SetStateAction<boolean>>
  setIdentifiersError: React.Dispatch<React.SetStateAction<boolean>>
  setSubmitEventHandler: React.Dispatch<React.SetStateAction<[null] | [() => void]>>
  idTypesOptions: {
    value: string
    label: string
  }[]
}

const IdentificationSection: React.FC<Props> = ({
  values,
  onChangeValue,
  isSpainCompany,
  readonly,
  employee,
  errors,
  setSubmitEventHandler,
  idTypesOptions,
  setIdentifiersDirty,
  setIdentifiersError,
}) => {
  const hasContractCue = useHasContractCue()
  const isNie = isSpainCompany ? values.identifierType === 'nie' : false
  const legalEntity = employeeLegalEntity(employee)
  const countryEnumValue: Country = legalEntity.country as Country

  const isEligibleForPayroll = employee.isEligibleForPayroll

  const identifierPlaceholder =
    countryEnumValue &&
    getPlaceholderByCountryAndIdType(countryEnumValue, values.identifierType as IdentificationType)

  const securityNumberPlaceholder =
    countryEnumValue &&
    getPlaceholderByCountryAndIdType(countryEnumValue, 'social_security_number' as SpainIdType)

  const identifierMaxLength =
    countryEnumValue &&
    getMaxLengthByCountryAndIdType(countryEnumValue, values.identifierType as IdentificationType)

  const securityNumberMaxLength =
    countryEnumValue &&
    getMaxLengthByCountryAndIdType(countryEnumValue, 'social_security_number' as SpainIdType)

  return (
    <Box gap="s24" paddingY="s24">
      <Box
        paddingBottom="s8"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey300',
          },
        }}
      >
        <Text.Header2 color="grey1000" weight="medium">
          {i18n.t('employee_form.identification')}
        </Text.Header2>
      </Box>
      <Box flexDirection="row" gap="s24">
        {idTypesOptions.length > 0 && (
          <Select
            mandatory={hasContractCue('identifier_type')}
            label={i18n.t('employee_form.identifier_type')}
            options={idTypesOptions}
            readonly={readonly}
            defaultValue={values.identifierType}
            error={errors.identifierType}
            onChange={(value) => onChangeValue('identifierType', value)}
            none={i18n.t('common.choose')}
          />
        )}
        <TextField
          label={i18n.t('employee_form.identifier')}
          value={values.identifier}
          readonly={readonly}
          onChange={(value) => onChangeValue('identifier', value)}
          error={errors.identifier}
          placeholder={identifierPlaceholder}
          maxLength={identifierMaxLength}
        />
        {isSpainCompany && (
          <TextField
            mandatory={hasContractCue('social_security_number')}
            avoidPasswordManagers
            label={i18n.t('employee_form.social_security_number')}
            value={values.socialSecurityNumber}
            readonly={readonly}
            onChange={(value) => onChangeValue('socialSecurityNumber', value)}
            error={
              errors.socialSecurityNumber &&
              i18n.t(
                'add_employee_modal.personal_details_step.fields.social_security_number.invalid_error'
              )
            }
            placeholder={securityNumberPlaceholder}
            maxLength={securityNumberMaxLength}
          />
        )}
      </Box>
      <Box flexDirection="row" gap="s24">
        <Identifiers
          employeeId={employee.id}
          country={legalEntity.country}
          mandatory={isEligibleForPayroll}
          readonly={readonly}
          setSubmitEventHandler={(onSubmit: (() => void) | null) =>
            setSubmitEventHandler([onSubmit])
          }
          setIdentifiersDirty={setIdentifiersDirty}
          setIdentifiersError={setIdentifiersError}
        />
      </Box>
      {isNie && (
        <TogglerCheckbox
          value={values.hasWorkPermit}
          onChange={() => onChangeValue('hasWorkPermit', !values.hasWorkPermit)}
          label={i18n.t('employee_form.work_permit.label')}
        />
      )}
    </Box>
  )
}

export default observer(IdentificationSection)
